import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Submitted", "Reviewing", "More Information Needed", "Approved", "Scheduled", "In Progress", "Waiting on Customer", "Waiting on Parts", "Ready for Pickup", "Completed", "Cancelled"];

export default function Requests() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canDelete = ["owner", "admin"].includes(user?.role);
  const { data: requests } = useQuery({ queryKey: ["requests"], queryFn: () => api.get("/service-requests").then(r => r.data.requests) });
  const setStatus = async (id, status) => {
    try { await api.put(`/service-requests/${id}/status`, null, { params: { status } }); qc.invalidateQueries({ queryKey: ["requests"] }); toast.success("Status updated"); }
    catch (e) { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    await api.delete(`/service-requests/${id}`);
    qc.invalidateQueries({ queryKey: ["requests"] });
    toast.success("Request deleted");
  };
  return (
    <div>
      <PageTitle title="Service Requests" subtitle="Incoming requests from the website & Nexus" />
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="requests-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
            <tr><th className="text-left p-3">Number</th><th className="text-left p-3">Name</th><th className="text-left p-3">Type</th><th className="text-left p-3">Urgency</th><th className="text-left p-3">Description</th><th className="text-left p-3">Status</th>{canDelete && <th className="text-left p-3"></th>}</tr>
          </thead>
          <tbody>
            {(requests || []).length === 0 ? <tr><td colSpan={canDelete ? 7 : 6} className="p-8 text-center text-slate-400">No requests yet.</td></tr> :
              (requests || []).map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`request-row-${r.id}`}>
                  <td className="p-3 font-mono text-xs text-slate-500">{r.requestNumber}</td>
                  <td className="p-3 font-medium text-navy">{r.fullName}<span className="block text-xs text-slate-400">{r.email}</span></td>
                  <td className="p-3 text-slate-600">{r.serviceType}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${r.urgency === "Emergency" ? "bg-red-100 text-red-700" : r.urgency === "High" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{r.urgency}</span></td>
                  <td className="p-3 text-slate-600 max-w-xs truncate">{r.description}</td>
                  <td className="p-3"><select value={r.status} onChange={e => setStatus(r.id, e.target.value)} className="text-xs rounded-md border border-slate-200 px-2 py-1" data-testid={`request-status-${r.id}`}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></td>
                  {canDelete && <td className="p-3"><ConfirmDelete onConfirm={() => remove(r.id)} title="Delete this request?" description={`Request ${r.requestNumber} from ${r.fullName} will be removed.`} testid={`request-delete-${r.id}`} /></td>}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
